import React, { MouseEvent } from 'react';
import './styles/App.scss';
import LoadingSpinner from './components/loading-spinner';
import { AppContext, AppContextInterface } from './app-context-provider';
import { Helmet } from 'react-helmet';
import { getStyles, getChildren, getDataById } from './helpers/api-helpers';
import SideMenu from './components/side-menu';
import TopMenu from './components/top-menu';
import ComponentSwitcher from './components/component-switcher';
import { inView, inViewBottom } from './helpers/layout-helpers';
import FloatingArrow from './components/floating-arrow';
import MainLogo from './nav/main-logo';
import Hotkeys from 'react-hot-keys';
import { BsArrowUp } from 'react-icons/bs';

export interface AppProps {
  nodeId: number;
}

export interface MenuItem {}

export interface AppState extends AppContextInterface {
  loading: boolean;
  navOpen: boolean;
  base: string;
  cssVars: string;
  showFloatingArrow: boolean;
}

class App extends React.Component<AppProps, AppState> {
  constructor(props: AppProps) {
    super(props);

    this.toggleNav = this.toggleNav.bind(this);
    this.onScroll = this.onScroll.bind(this);
    let base = document.querySelector('base');
    this.state = {
      base: base?.href || '',
      id: 0,
      loading: true,
      navOpen: false,
      styles: null,
      pageTree: null,
      cssVars: '',
      currentSection: 'site-intro',
      showFloatingArrow: false,
    };
  }

  public state: AppState;

  componentDidMount() {
    this.getContext();

    window.addEventListener('scroll', this.onScroll);
    setTimeout(() => {
      this.setState({ showFloatingArrow: true });
    }, 2000);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll);
  }

  async getContext() {
    const styles = await getStyles(this.props.nodeId);
    const pageData = await getDataById(this.props.nodeId);
    const children = await getChildren(pageData.id);
    // console.log(pageData);
    this.setState({
      loading: false,
      cssVars: styles.stylesheet,
      styles: styles.styleObject,
      pageData,
      children,
    });
  }

  toggleNav(event: MouseEvent<HTMLElement>): void {
    this.setState((prevState) => ({
      navOpen: !prevState.navOpen,
    }));
  }

  onScroll(e: any) {
    const sections = document.querySelectorAll('.can-scroll-to');
    const siteIntro = document.querySelector('header');
    if (!sections) return;

    if (siteIntro && inViewBottom(siteIntro)) {
      return this.setState({ currentSection: 'site-intro' });
    }

    sections.forEach((section) => {
      //switch to children if theres nested sections

      if (inView(section)) {
        const sectionChildren = section.querySelectorAll('.can-scroll-to');
        if (sectionChildren?.length > 0) {
          sectionChildren.forEach((childSection) => {
            if (inView(childSection)) {
              return this.setState({
                currentSection: childSection.id,
              });
            }
          });
        } else {
          return this.setState({ currentSection: section.id });
        }
      }
    });
  }

  // createLinks(): JSX.Element[] {
  //     return this.state.customerData!.pages.slice(1, this.state.customerData!.pages?.length).map((page: Page) => {
  //         return <Link key={page.key} to={page.url}>{page.name}</Link>
  //     })
  // }

  render() {
    if (this.state.loading) {
      return <LoadingSpinner />;
    }

    return (
      <AppContext.Provider value={this.state}>
        <Hotkeys
          keyName='alt+shift+g'
          allowRepeat={true}
          onKeyDown={() => {
            const w = window.open(
              `https://github.com/aksellsor/07_cra_designmanual/actions`,
              '_blank'
            );

            if (w) {
              w.focus(); // okay now
            }
          }}></Hotkeys>
        <Hotkeys
          keyName='alt+shift+u'
          allowRepeat={true}
          onKeyDown={() => {
            const w = window.open(
              `https://digitalprofilmanual.no/umbraco#/content/content/edit/${document
                .querySelector('[data-node-id]')
                ?.getAttribute('data-node-id')}`,
              '_blank'
            );

            if (w) {
              w.focus(); // okay now
            }
          }}></Hotkeys>
        <Helmet
          style={[
            {
              cssText: this.state.cssVars,
            },
          ]}>
          <meta charSet='utf-8' />
          <title>{this.state.pageData?.title}</title>
          <link
            rel='icon'
            type='image/png'
            href={
              this.state.pageData?.favicon?.url +
              '?format=png&width=64&height=64'
            }
            sizes='16x16'
          />
        </Helmet>
        <main
          className={`${
            this.state.pageData?.globalFont
              ? 'has-global-font'
              : 'no-global-font'
          }`}
          style={
            this.state.pageData?.globalFont?.fontFamilyName
              ? {
                  fontFamily: this.state.pageData?.globalFont?.fontFamilyName,
                }
              : {}
          }>
          <header>
            {this.state.pageData?.bakgrunnsbilde?.url && (
              <div
                className='header__bg'
                style={{
                  backgroundImage: `url("${this.state.pageData?.bakgrunnsbilde?.url}")`,
                }}
              />
            )}
            <div
              className='site-intro'
              style={{
                backgroundColor: this.state.styles?.background.light,
              }}>
              <MainLogo />
              <div>
                <p style={{ display: 'block' }}>
                  {this.state.pageData?.pagePreTitle || 'Profilmanualen til'}
                </p>
                <h1
                  className='left-top-title'
                  style={
                    /* // this.state.styles?.heading.vibrant
  									// 	? {
  									// 			color: this.state.styles?.heading.vibrant,
  									// 	  }
  									// 	: */ { color: '#000', marginTop: 0 }
                  }>
                  {this.state.pageData?.pageTitle || this.state.pageData?.name}
                </h1>
              </div>
              {this.state.showFloatingArrow && <FloatingArrow />}
            </div>
            <nav
              className='right-top-nav'
              style={{
                backgroundColor: this.state.styles?.background.dark,
              }}>
              <div>
                <h2
                  className='right-top-nav__title'
                  style={{
                    color: this.state.styles?.heading.light,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: `${
                      this.state.pageData.categoryIntroText?.length > 0
                        ? this.state.pageData.categoryIntroText
                        : 'Velg kategori, <br /> eller scroll deg gjennom siden!'
                    }`,
                  }}></h2>
                <TopMenu
                  pages={this.state.children}
                  color={this.state.styles?.bodyText.light}
                />
              </div>
              {this.state.pageData.email ? (
                <div
                  style={{
                    color: this.state.styles?.heading.light,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: this.state.pageData.email,
                  }}></div>
              ) : (
                ''
              )}
            </nav>
          </header>

          <div className='main-container'>
            <SideMenu
              // currentSection={this.state.currentSection}
              pages={this.state.children}
            />
            <div className='content'>
              {this.state.children?.map((section: any) => (
                <React.Fragment key={section.key + '-fragment'}>
                  <ComponentSwitcher {...section} />
                  <hr key={section.key + '-hr'} className='section-separator' />
                </React.Fragment>
              ))}
            </div>
          </div>

          <footer>
            <MainLogo />
            <BsArrowUp
              className='scroll-top-btn'
              onClick={() => {
                window.scrollTo(0, 0);
              }}
            />
            {/* <h3>Footer Text</h3> */}
          </footer>
          {this.state.pageData?.bakgrunnsbilde?.url && (
            <div
              className='main-container__bg'
              style={{
                backgroundImage: `url("${this.state.pageData?.bakgrunnsbilde?.url}")`,
              }}
            />
          )}
        </main>

        {/* <div className="App" style={{ color: this.state.customerData?.colors.textColors.primaryColor }}>
                    <Router>
                        <header className="App-header">
                            <NavBar open={this.state.navOpen} toggleNav={this.toggleNav} />
                            <NavLinks open={this.state.navOpen}  toggleNav={this.toggleNav} links={this.createLinks()} />

                        </header>
                        <Switch>
                            <Route path="*" component={RoutingComponent}>
                            </Route>
                        </Switch>
                    </Router>
                </div> */}
      </AppContext.Provider>
    );
  }
}

export default App;
